import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import ErrorBoundary from '../components/common/ErrorBoundary';
import { toast } from 'sonner';
import { Trophy, Star, Award, Briefcase, MessageSquare, MapPin, Flame, Zap, Filter, Crown } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

function Leaderboard() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('xp');
  const [region, setRegion] = useState('');
  const [industry, setIndustry] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const fetchLeaderboard = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('sort_by', sortBy);
      params.append('limit', '20');
      if (region) params.append('region', region);
      if (industry) params.append('industry', industry);

      const res = await fetch(`${API}/leaderboard?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      setEntries(data);
    } catch (error) {
      toast.error('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  }, [token, sortBy, region, industry]);

  useEffect(() => { fetchLeaderboard(); }, [fetchLeaderboard]);

  const badgeConfig = {
    GOLD: { label: 'Gold', color: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30' },
    SILVER: { label: 'Silver', color: 'text-slate-300 bg-slate-300/10 border-slate-300/30' },
    BRONZE: { label: 'Bronze', color: 'text-amber-600 bg-amber-600/10 border-amber-600/30' }
  };

  const labelConfig = {
    'Top Rated': { icon: Star, color: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30' },
    'Most Deals Won': { icon: Trophy, color: 'text-green-400 bg-green-400/10 border-green-400/30' },
    'Most Reviewed': { icon: MessageSquare, color: 'text-blue-400 bg-blue-400/10 border-blue-400/30' }
  };

  const sortOptions = [
    { value: 'xp', label: 'XP Points', icon: Zap },
    { value: 'deals', label: 'Deals Won', icon: Briefcase },
    { value: 'rating', label: 'Star Rating', icon: Star },
    { value: 'reviews', label: 'Review Count', icon: MessageSquare },
  ];

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-brand-bg" data-testid="leaderboard-page">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <Trophy className="w-7 h-7 text-brand-gold" />
              <h1 className="font-display text-2xl sm:text-3xl text-brand-text tracking-wide">Leaderboard</h1>
            </div>
            <p className="text-brand-muted text-sm">Top performing salespeople on Sale Sphere</p>
          </div>

          {/* Sort Tabs */}
          <div className="flex gap-1 mb-4 overflow-x-auto">
            {sortOptions.map((opt) => {
              const Icon = opt.icon;
              return (
                <button
                  key={opt.value}
                  onClick={() => setSortBy(opt.value)}
                  data-testid={`leaderboard-sort-${opt.value}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                    sortBy === opt.value
                      ? 'bg-brand-gold/20 text-brand-gold border border-brand-gold/30'
                      : 'bg-[#111317] text-brand-muted border border-white/10 hover:text-brand-text'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {opt.label}
                </button>
              );
            })}
            <button
              onClick={() => setShowFilters(!showFilters)}
              data-testid="leaderboard-filter-toggle"
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm border whitespace-nowrap ${
                showFilters ? 'bg-brand-gold/20 border-brand-gold/30 text-brand-gold' : 'bg-[#111317] border-white/10 text-brand-muted'
              }`}
            >
              <Filter className="w-3.5 h-3.5" />
              Filters
            </button>
          </div>

          {/* Filters */}
          {showFilters && (
            <div className="rounded-xl border border-white/10 bg-[#111317] p-4 mb-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-brand-muted mb-1">Region</label>
                  <input
                    type="text"
                    placeholder="e.g. Ontario"
                    value={region}
                    onChange={(e) => setRegion(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-brand-text text-sm"
                    data-testid="leaderboard-filter-region"
                  />
                </div>
                <div>
                  <label className="block text-xs text-brand-muted mb-1">Industry</label>
                  <input
                    type="text"
                    placeholder="e.g. SaaS"
                    value={industry}
                    onChange={(e) => setIndustry(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-brand-text text-sm"
                    data-testid="leaderboard-filter-industry"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Leaderboard List */}
          {loading ? (
            <div className="text-brand-muted text-center py-20">Loading leaderboard...</div>
          ) : entries.length === 0 ? (
            <div className="text-center py-20">
              <Trophy className="w-12 h-12 text-brand-muted/40 mx-auto mb-3" />
              <p className="text-brand-muted">No salespeople found</p>
            </div>
          ) : (
            <div className="space-y-2">
              {entries.map((entry, idx) => {
                const b = entry.badge ? badgeConfig[entry.badge] : null;
                const isTop3 = idx < 3;
                return (
                  <div
                    key={entry.id}
                    onClick={() => navigate(`/salespeople/${entry.id}`)}
                    data-testid="leaderboard-entry"
                    className={`group flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition-all duration-200 ${
                      isTop3
                        ? 'bg-[#111317] border-brand-gold/20 hover:border-brand-gold/40'
                        : 'bg-[#111317] border-white/10 hover:border-white/20'
                    }`}
                  >
                    {/* Rank */}
                    <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${
                      idx === 0 ? 'bg-brand-gold text-black' :
                      idx === 1 ? 'bg-gray-400 text-black' :
                      idx === 2 ? 'bg-amber-700 text-white' :
                      'bg-zinc-800 text-brand-muted'
                    }`}>
                      {idx === 0 ? <Crown className="w-5 h-5" /> : `#${idx + 1}`}
                    </div>

                    {/* Avatar */}
                    {entry.avatar ? (
                      <img src={entry.avatar} alt={entry.name} className="w-12 h-12 rounded-full object-cover border border-white/10 flex-shrink-0" />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-brand-gold/20 flex items-center justify-center text-brand-gold font-semibold text-lg flex-shrink-0">
                        {entry.name?.charAt(0)?.toUpperCase()}
                      </div>
                    )}

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-brand-text font-semibold group-hover:text-brand-gold transition-colors">{entry.name}</span>
                        {b && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${b.color}`}>
                            <Award className="w-2.5 h-2.5 inline mr-0.5" />{b.label}
                          </span>
                        )}
                        {/* Top Performer Labels */}
                        {entry.labels?.map((label, i) => {
                          const lc = labelConfig[label];
                          if (!lc) return null;
                          const LIcon = lc.icon;
                          return (
                            <span key={i} className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${lc.color}`} data-testid="leaderboard-label">
                              <LIcon className="w-2.5 h-2.5 inline mr-0.5" />{label}
                            </span>
                          );
                        })}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-brand-muted flex-wrap">
                        {entry.region && (
                          <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{entry.region}</span>
                        )}
                        {entry.streak > 0 && (
                          <span className="flex items-center gap-0.5 text-orange-400"><Flame className="w-3 h-3" />{entry.streak} mo streak</span>
                        )}
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="flex items-center gap-5 flex-shrink-0">
                      <div className="text-center hidden sm:block">
                        <div className="flex items-center gap-1 justify-center">
                          <Zap className="w-3.5 h-3.5 text-purple-400" />
                          <span className="text-brand-text font-bold">{entry.xp}</span>
                        </div>
                        <div className="text-[10px] text-brand-muted">XP</div>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center gap-1 justify-center">
                          <Star className="w-3.5 h-3.5 text-brand-gold fill-brand-gold" />
                          <span className="text-brand-text font-bold">{entry.avg_rating.toFixed(1)}</span>
                        </div>
                        <div className="text-[10px] text-brand-muted">{entry.review_count} reviews</div>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center gap-1 justify-center">
                          <Briefcase className="w-3.5 h-3.5 text-green-400" />
                          <span className="text-brand-text font-bold">{entry.deals_won}</span>
                        </div>
                        <div className="text-[10px] text-brand-muted">deals</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </ErrorBoundary>
  );
}

export default Leaderboard;
